import { useTranslation } from "react-i18next"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { CardHeader } from "@/components/ui/card"
import { UserProvider } from "@/components/user-wrapper"
import { formatDateString } from "@/lib/format-date"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { ChatTranscriptContainer } from "@/modules/patients/detail/progress-note/components/chat-transcript-container"
import { CallerRoleIcon } from "@/modules/patients/detail/progress-note/lib/get-caller-role-icon"
import { getProgressNoteChannel } from "@/modules/patients/detail/progress-note/lib/get-progress-note-channel"
import { ProgressNoteActions } from "@/modules/patients/detail/progress-note/progress-note-actions"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

interface AdministrativeProgressNoteCardHeaderProps {
  isEpisodeOfCareUpdatable?: boolean
  parentEpisodeOfCareFragmentReference: EpisodeOfCareFragment$key
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  isEditWithin24HoursAfterCloseMode: boolean
  onEditWithin24HoursAfterCloseModeChange: (value: boolean) => void
}

export const AdministrativeProgressNoteCardHeader = ({
  isEpisodeOfCareUpdatable,
  parentEpisodeOfCareFragmentReference,
  episodeOfCareProgressNoteData,
  isEditWithin24HoursAfterCloseMode,
  onEditWithin24HoursAfterCloseModeChange,
}: AdministrativeProgressNoteCardHeaderProps) => {
  const patientId = usePatientParam()
  const { t } = useTranslation()

  const {
    id: progressNoteId,
    appointment: { id: appointmentId, service, hasChatTranscript, channel, access, callerRole },
    createdAt,
    createdBy,
  } = episodeOfCareProgressNoteData

  return (
    <CardHeader
      className="flex items-center justify-between gap-4 border-b py-3 pr-2 pl-4"
      data-testid="progress-note-header">
      <div className="flex items-center gap-4">
        <UserProvider
          userName={createdBy}
          render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={progressNoteId} size="md" />}
        />
        <div className="grid items-center gap-1">
          <div className="grid w-fit max-w-100">
            <span className="text-on-surface truncate text-base font-medium">
              {t(`scheduling.waitingRoom.service.${service}`)}
            </span>
          </div>
          <div className="text-on-surface-subdued -mt-2 flex items-center gap-1 text-xs">
            {formatDateString(createdAt)}
            {hasChatTranscript && <ChatTranscriptContainer patientId={patientId} appointmentId={appointmentId} />}
            <span>{" • "}</span>
            {getProgressNoteChannel(channel)}
            <CallerRoleIcon callerRole={callerRole} />
            <span>{" • "}</span>
            {t(`scheduling.waitingRoom.access.${access}`)}
          </div>
        </div>
      </div>
      <ProgressNoteActions
        isEpisodeOfCareUpdatable={isEpisodeOfCareUpdatable}
        parentEpisodeOfCareFragmentReference={parentEpisodeOfCareFragmentReference}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        isEditWithin24HoursAfterCloseMode={isEditWithin24HoursAfterCloseMode}
        onEditWithin24HoursAfterCloseModeChange={onEditWithin24HoursAfterCloseModeChange}
      />
    </CardHeader>
  )
}
